<template>
  <div>
    <slot
      :browse="browse"
      :model="model"
      :uploading="uploading"
      :remove="remove"
      :remove-all="removeAll"
    >
      <label v-if="label">
        {{ label }} <span v-if="required" class="text-danger"> * </span>
      </label>

      <div class="uploader-grid-container">
        <div
          v-for="file in model"
          :key="file"
          class="upload-item user-select-none cursor-pointer"
        >
          <brb-media
            v-if="file.match(/\.(jpeg|jpg|png)$/) !== null"
            :src="file"
            width="100"
            height="100"
            style="width: 100px; height: 100px; object-fit: cover"
            show-image-preview
          />

          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center w-100 h-100"
            style="gap: 8px"
            @click="openStaticLink(file)"
          >
            <font-awesome-icon icon="file" size="2x" />

            <span class="text-muted sm">{{ file.split('.').pop() }}</span>
          </div>

          <div class="remove-file" @click="remove(file)">
            <font-awesome-icon icon="trash-can" />
          </div>
        </div>

        <div
          v-if="showBrowseButton"
          class="upload-item dashed-border d-flex flex-column align-items-center justify-content-center user-select-none cursor-pointer"
          style="gap: 8px"
          :tab-index="tabIndex"
          @click="browse"
        >
          <template v-if="uploading">
            <b-progress
              :value="uploadProgress"
              class="align-self-stretch mx-3"
              variant="brb-secondary"
            />

            <span class="small">{{ uploadProgress }}</span>
          </template>

          <template v-else>
            <font-awesome-icon icon="plus" size="lg" />

            <span class="small">
              {{ $t('forms.chooseFile') }}
            </span>
          </template>
        </div>
      </div>

      <ul v-if="computedErrors.length" class="px-0 py-2">
        <li
          v-for="(error, index) in computedErrors"
          :key="`error-${index}`"
          class="text-danger small"
          style="list-style: none"
        >
          {{ error }}
        </li>
      </ul>
    </slot>

    <input
      v-show="false"
      ref="hidden-file-input"
      type="file"
      :accept="inputAccept"
      :multiple="multiple"
      @change="onFileInputChange"
    />
  </div>
</template>

<script>
export default {
  name: 'BrbUploader',
  props: {
    value: {
      type: [String, null],
      default: null
    },
    accept: {
      type: [String, Array],
      default: () => ['.png', '.jpg', '.jpeg']
    },
    maxSizeByte: {
      type: Number,
      default: 10485760 //  = 10 MB
    },
    maxUploadCount: {
      type: Number,
      default: 10
    },
    label: String,
    required: Boolean,
    squareImage: Boolean,
    multiple: Boolean,
    tabIndex: String,
    error: String
  },
  data() {
    return {
      errorStack: [],
      uploading: false,
      uploadProgress: 0
    }
  },
  computed: {
    model: {
      get() {
        return this.value ? this.value.split(',') : []
      },
      set(value) {
        this.$emit('input', value && value.length ? value.toString() : null)
      }
    },
    acceptArray() {
      if (this.accept && this.accept !== '*.*') {
        if (Array.isArray(this.accept)) {
          return this.accept.map((item) => item.replace('.', '').toLowerCase())
        }

        return this.accept
          .split(',')
          .map((item) => item.replace('.', '').toLowerCase())
      }

      return []
    },
    inputAccept() {
      return this.acceptArray
        .map((item) => (item.includes('/*') ? item : `.${item}`))
        .toString()
    },
    showBrowseButton() {
      return this.multiple
        ? this.model.length < this.maxUploadCount
        : !this.model.length
    },
    computedErrors() {
      return this.error ? [this.error, ...this.errorStack] : this.errorStack
    }
  },
  methods: {
    browse() {
      if (!this.uploading && this.$refs['hidden-file-input']) {
        this.$refs['hidden-file-input'].click()
      }
    },
    checkMaxUploadCount(files) {
      const valid = files.length + this.model.length <= this.maxUploadCount

      if (!valid) {
        this.errorStack.push(
          this.$t('forms.uploadCountLimit', { count: this.maxUploadCount })
        )
      }

      return valid
    },
    onFileInputChange(event) {
      this.errorStack = []

      const files = Array.from(event.target.files)

      if (this.checkMaxUploadCount(files)) this.upload(files)
    },
    checkFileFormat(file) {
      if (this.accept === '*.*') return true

      const format = file.name.split('.').pop().toLowerCase()
      const valid = this.acceptArray.includes(format)

      if (!valid) {
        this.errorStack.push(
          this.$t('forms.file_extension_not_accepted', {
            fileName: file.name,
            accepted: this.inputAccept
          })
        )
      }

      return valid
    },
    checkFileSize(file) {
      const valid = file.size <= this.maxSizeByte
      if (!valid)
        this.errorStack.push(
          this.$t('forms.file_max_size_exceeded', {
            fileName: file.name,
            maxSize: Math.floor(this.maxSizeByte / 1024)
          })
        )
      return valid
    },
    checkSquareImage(file) {
      const _this = this
      return new Promise((resolve) => {
        const image = new Image()
        image.onload = function () {
          const valid = this.width === this.height

          if (!valid) {
            const message = _this.$t(
              `forms.squareImageError.${_this.multiple ? 'multiple' : 'single'}`
            )
            if (!_this.errorStack.includes(message))
              _this.errorStack.push(message)
          }

          return resolve({ valid, file })
        }

        image.src = window.URL.createObjectURL(file)
      })
    },
    validate(files) {
      const validateArray = []
      files.forEach((file) => {
        const formatAndSizeValid =
          this.checkFileFormat(file) && this.checkFileSize(file)

        if (formatAndSizeValid) {
          if (file.type.match(/image.*/) && this.squareImage) {
            validateArray.push(this.checkSquareImage(file))
          } else {
            validateArray.push(Promise.resolve({ valid: true, file }))
          }
        } else {
          validateArray.push(Promise.resolve({ valid: false, file }))
        }
      })

      return Promise.all(validateArray).then((transformedFiles) => {
        const validFiles = transformedFiles.reduce((validFiles, item) => {
          if (item.valid) validFiles.push(item.file)
          return validFiles
        }, [])

        if (validFiles.length) return Promise.resolve(validFiles)
        return Promise.reject(new Error('No valid file found'))
      })
    },
    upload(files) {
      return this.validate(files).then((validFiles) => {
        const formData = new FormData()

        validFiles.forEach((file) => formData.append('files', file))

        this.uploading = true

        return this.$api.file
          .upload(formData, false, this.onUploadProgress)
          .then((response) => {
            this.model = this.multiple ? this.model.concat(response) : response

            return response
          })
          .catch((error) => {
            if (error.error) {
              this.errorStack.push(this.$t('forms.invalidFileUpload'))
            } else {
              this.$showError(error.toString())
            }
          })
          .finally(() => {
            this.resetFileInput()
            this.uploadProgress = 0
            this.uploading = false
          })
      })
    },
    remove(file, showConfig = true) {
      if (showConfig)
        return this.$confirm(this.$t('forms.areYouSureToRemoveThisFile')).then(
          (confirm) => {
            if (confirm) return this.deleteFile(file)
            return false
          }
        )

      return this.deleteFile(file)
    },
    removeAll() {
      if (this.model && this.model.length)
        this.model.forEach((file) => this.deleteFile(file))
    },
    deleteFile(file) {
      if (file.startsWith('/temp')) {
        const fileName = file.split('/').pop()
        this.$api.file.delete(fileName, true)
      }

      this.model = this.model.filter((item) => item !== file)
    },
    onUploadProgress(event) {
      this.uploadProgress = parseInt((event.loaded * 100) / event.total)
      this.$emit('progress', this.uploadProgress)
    },
    resetFileInput() {
      if (this.$refs['hidden-file-input'])
        this.$refs['hidden-file-input'].value = ''
    },
    openStaticLink(path) {
      window.open(this.$config.files.createStaticUrl(path), '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.uploader-grid-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  max-height: 150px;
  overflow-y: auto;

  .upload-item {
    position: relative;
    height: 100px;
    width: 100px;
    color: #515151;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 1px solid #515151;

    &.dashed-border {
      border-style: dashed;
    }

    .remove-file {
      position: absolute;
      width: 24px;
      height: 24px;
      bottom: 8px;
      right: 8px;
      background: rgba(25, 28, 27, 0.7);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;

      font-size: 12px;
    }
  }
}
</style>
